/**
 * Replaces {domain} and {keyword} placeholders in a favorite site template.
 *
 * {keyword}  literal value, URL-encoded (good for search engines and chat tools).
 * {domain}   normalised for domain registrar search:
 *              1. spaces stripped       ("Brew Box"     -> "BrewBox")
 *              2. lowercased            ("BrewBox"      -> "brewbox")
 *              3. ".com" appended when there's no TLD   ("brewbox" -> "brewbox.com")
 *            Then URL-encoded. If the value already contains a "." we trust it
 *            (so "BrewBox.io" stays "brewbox.io").
 *
 * Values are URL-encoded so user-provided strings can't break the URL.
 */
export function buildFavoriteUrl(
  template: string,
  value: string,
  keyword?: string
): string {
  // Defensive: reject templates that aren't http(s) URLs. A user creating
  // a favorite with template "javascript:alert(1)" would otherwise produce
  // a self-clickable XSS on their own session via SelectedDomain's
  // <a href={url}>. Self-attack only (favorites are per-device localStorage,
  // no cross-user vector today) but a one-line guard closes it and is
  // forward-safe for any future "share favorites" feature.
  if (!/^https?:\/\//i.test(template)) {
    return '';
  }

  const raw = value || '';

  let domainValue = raw.replace(/\s+/g, '').toLowerCase();
  if (domainValue && !domainValue.includes('.')) {
    domainValue = `${domainValue}.com`;
  }
  const safeDomain = encodeURIComponent(domainValue);

  const safeKeyword = encodeURIComponent(keyword || raw);

  return template
    .replaceAll('{domain}', safeDomain)
    .replaceAll('{keyword}', safeKeyword);
}
